import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { Ich, Modul, Umgebung } from '../shared/rollen';
import { api, ApiFehler, NICHT_ANGEMELDET } from './api';
import { gehe, useOrt } from './ort';
import { IN_ETAPPE, navigation } from './navigation';
import { Hinweise } from './komponenten/Grundbausteine';
import { Rahmen } from './komponenten/Rahmen';
import { Anmeldung } from './seiten/Anmeldung';
import { Platzhalter } from './seiten/Platzhalter';
import { Uebersicht } from './seiten/akquise/Uebersicht';
import { Listen } from './seiten/akquise/Listen';
import { Pruefen } from './seiten/akquise/Pruefen';
import { Beschaffung } from './seiten/akquise/Beschaffung';
import { Kampagnen } from './seiten/akquise/Kampagnen';
import { Einstellungen } from './seiten/akquise/Einstellungen';
import { useDaten, ZAEHLER_NEU } from './daten';
import { GebuchteTermine, OpenerArchiv, OpenerAufgaben, OpenerKampagnen } from './seiten/crm/Opener';
import { Material, MeineVerguetung, Nachrichten, Postfach } from './seiten/crm/Gemeinsam';
import { Kundenansicht, MeineKunden, TerminAufgaben, TerminSeite } from './seiten/crm/Kunden';
import { BhBank, BhEinnahmen, BhUebersicht, Rechnungen } from './seiten/buchhaltung/Geld';
import { Ausgaben, BhBafa, BhEinstellungen, BhRetainer, BhTera, BhUst } from './seiten/buchhaltung/Rest';
import { KdEinstellungen, KdProjekte, KdUebersicht, KdVerguetung } from './seiten/kunden/Projekte';
import { KdKunden, KdPotenziale, KdVertragsphase, KdWebsites } from './seiten/kunden/Bestand';
import { CkIdeen, CkKalender, CkProjekte, CkReferenzen, CkTodos, CkUebersicht, CkZiele } from './seiten/cockpit/Cockpit';
import { CkVorlagen } from './seiten/cockpit/Vorlagen';
import { Abrechnung, AdminAufgaben, AdminEinstellungen, AdminMaterial, AdminUebersicht, AlleKunden, Team, Vertragsphase } from './seiten/crm/Admin';

type Stand = { art: 'laden' } | { art: 'abgemeldet' } | { art: 'fehler'; text: string } | { art: 'da'; ich: Ich; umgebung: Umgebung };

// Gebaute Seiten je Modul; alles andere zeigt den Platzhalter
const SEITEN: Partial<Record<Modul, Record<string, (rest: string[]) => ReactNode>>> = {
  akquise: { uebersicht: () => <Uebersicht />, listen: rest => <Listen listeId={rest[0]} />, pruefen: () => <Pruefen />, beschaffung: () => <Beschaffung />,
    kampagnen: rest => <Kampagnen rest={rest} />, einstellungen: rest => <Einstellungen bereich={rest[0]} /> }
};

/** Seiten des CRM je Rolle (Opener, Closer und Berater, Admin); was noch nicht gebaut ist, zeigt den Platzhalter. */
function crmSeite(ich: Ich, id: string, rest: string[]): ReactNode | null {
  const admin = ich.rollen.includes('admin'), termine = !admin && ich.rollen.some(r => r === 'closer' || r === 'berater'), opener = !admin && !termine;
  if (opener) {
    if (id === 'aufgaben') return <OpenerAufgaben name={ich.name} />;
    if (id === 'kampagnen') return <OpenerKampagnen rest={rest} />;
    if (id === 'termine') return <GebuchteTermine />;
    if (id === 'archiv') return <OpenerArchiv />;
  }
  if (termine) {
    const rolle = ich.rollen.includes('closer') && ich.rollen.includes('berater') ? 'cb' : ich.rollen.includes('berater') ? 'berater' : 'closer';
    if (id === 'aufgaben') return <TerminAufgaben name={ich.name} beide={rolle === 'cb'} />;
    if (id === 'termine') return <TerminSeite rolle={rolle} />;
    if (id === 'kunden') return rest[0] ? <Kundenansicht kid={rest[0]} zurueck="/crm/kunden" /> : <MeineKunden rolle={rolle} />;
  }
  if (admin) {
    if (id === 'uebersicht') return <AdminUebersicht name={ich.name} />;
    if (id === 'kunden') return rest[0] ? <Kundenansicht kid={rest[0]} zurueck="/crm/kunden" /> : <AlleKunden />;
    if (id === 'aufgaben') return <AdminAufgaben />;
    // Leon ist auch Closer und Berater seiner eigenen Kunden (Leon 2026-10-30)
    if (id === 'meine') return rest[0] ? <Kundenansicht kid={rest[0]} zurueck="/crm/meine" /> : <MeineKunden rolle="cb" />;
    if (id === 'termine') return <TerminSeite rolle="cb" />;
    if (id === 'vertragsphase') return rest[0] ? <Kundenansicht kid={rest[0]} zurueck="/crm/vertragsphase" /> : <Vertragsphase />;
    if (id === 'abrechnung') return <Abrechnung />;
    if (id === 'team') return <Team rest={rest} ichId={ich.id} />;
    if (id === 'material') return <AdminMaterial />;
    if (id === 'einstellungen') return <AdminEinstellungen />;
  }
  if (id === 'nachrichten') return <Nachrichten rest={rest} opener={opener} />;
  if (id === 'material') return <Material rollen={ich.rollen} />;
  if (id === 'verguetung') return <MeineVerguetung />;
  if (id === 'postfach') return <Postfach rolle={admin ? 'admin' : termine ? 'termine' : 'opener'} />;
  return null;
}

/** Seiten der Buchhaltung (nur der Admin) */
function bhSeite(ich: Ich, umgebung: Umgebung, id: string): ReactNode | null {
  const S: Record<string, () => ReactNode> = {
    uebersicht: () => <BhUebersicht name={ich.name} />, rechnungen: () => <Rechnungen />, bank: () => <BhBank test={umgebung !== 'echt'} />, einnahmen: () => <BhEinnahmen />,
    ausgaben: () => <Ausgaben />, retainer: () => <BhRetainer />, bafa: () => <BhBafa />, umsatzsteuer: () => <BhUst />, tera: () => <BhTera />, einstellungen: () => <BhEinstellungen />
  };
  return S[id]?.() ?? null;
}

/** Seiten des Cockpits (nur Leon) */
function ckSeite(id: string, rest: string[]): ReactNode | null {
  const S: Record<string, () => ReactNode> = { uebersicht: () => <CkUebersicht />, todos: () => <CkTodos />, kalender: () => <CkKalender />, projekte: () => <CkProjekte />, ziele: () => <CkZiele />, ideen: () => <CkIdeen />, vorlagen: () => <CkVorlagen rest={rest} />, referenzen: () => <CkReferenzen kategorie={rest[0]} /> };
  return S[id]?.() ?? null;
}

/** Seiten des Moduls Kunden (Admin und Berater) */
function kdSeite(ich: Ich, id: string, rest: string[]): ReactNode | null {
  const admin = ich.rollen.includes('admin');
  const S: Record<string, () => ReactNode> = {
    uebersicht: () => <KdUebersicht name={ich.name} />, projekte: () => <KdProjekte admin={admin} />, kunden: () => <KdKunden admin={admin} rest={rest} />, potenziale: () => <KdPotenziale admin={admin} />,
    ...(admin ? { vertragsphase: () => <KdVertragsphase rest={rest} />, websites: () => <KdWebsites />, einstellungen: () => <KdEinstellungen /> } : { verguetung: () => <KdVerguetung name={ich.name} /> })
  };
  return S[id]?.() ?? null;
}

function Arbeitsflaeche({ ich, umgebung }: { ich: Ich; umgebung: Umgebung }) {
  const [m, a, ...rest] = useOrt();
  const modul = (ich.module as string[]).includes(m) ? (m as Modul) : ich.module[0];
  const punkte = navigation(modul, ich);
  const punkt = punkte.find(p => p.id === a) ?? punkte[0];
  useEffect(() => {
    if (m !== modul || a !== punkt.id) gehe(`/${modul}/${punkt.id}`, true);
  }, [m, a, modul, punkt.id]);
  const crm = modul === 'crm' ? crmSeite(ich, punkt.id, rest) : modul === 'buchhaltung' ? bhSeite(ich, umgebung, punkt.id) : modul === 'kunden' ? kdSeite(ich, punkt.id, rest) : modul === 'cockpit' ? ckSeite(punkt.id, rest) : null;
  const seite = SEITEN[modul]?.[punkt.id];
  // Zähler offener Arbeit in der Seitenleiste; bei jedem Seitenwechsel neu und nach Aktionen, die sie ändern
  const zaehler = useDaten<Record<string, number>>(modul === 'akquise' ? `/api/akquise/zaehler?s=${encodeURIComponent(punkt.id)}` : modul === 'crm' ? `/api/crm/zaehler?s=${encodeURIComponent(punkt.id)}` : modul === 'buchhaltung' ? `/api/buchhaltung/zaehler?s=${encodeURIComponent(punkt.id)}` : modul === 'kunden' ? `/api/kunden/zaehler?s=${encodeURIComponent(punkt.id)}` : modul === 'cockpit' ? `/api/cockpit/zaehler?s=${encodeURIComponent(punkt.id)}` : null);
  const zNeu = zaehler.neu;
  useEffect(() => { addEventListener(ZAEHLER_NEU, zNeu); return () => removeEventListener(ZAEHLER_NEU, zNeu); }, [zNeu]);
  return (
    <Rahmen ich={ich} umgebung={umgebung} modul={modul} ansicht={punkt.id} zaehler={zaehler.daten ?? {}}>
      <section key={`${modul}/${punkt.id}`} className="view enter" data-view={punkt.id}>
        {crm ?? (seite ? seite(rest) : <Platzhalter punkt={punkt} etappe={IN_ETAPPE[modul]} />)}
      </section>
    </Rahmen>
  );
}

export function App() {
  const [stand, setStand] = useState<Stand>({ art: 'laden' });
  const laden = useCallback(() => {
    api<{ ich: Ich; umgebung: Umgebung }>('/api/ich').then(
      d => setStand({ art: 'da', ich: d.ich, umgebung: d.umgebung }),
      e => setStand(e instanceof ApiFehler && e.status === 401 ? { art: 'abgemeldet' } : { art: 'fehler', text: (e as Error).message })
    );
  }, []);
  useEffect(() => {
    laden();
    const ab = () => setStand({ art: 'abgemeldet' });
    window.addEventListener(NICHT_ANGEMELDET, ab);
    return () => window.removeEventListener(NICHT_ANGEMELDET, ab);
  }, [laden]);

  if (stand.art === 'laden') return null;
  if (stand.art === 'abgemeldet') return <Anmeldung angemeldet={laden} />;
  if (stand.art === 'fehler') return <div className="login"><section className="panel"><h1>Nicht erreichbar</h1><p className="meta">{stand.text}</p><button className="btn" style={{ alignSelf: 'flex-start' }} onClick={laden}>Noch einmal versuchen</button></section></div>;
  return <Hinweise><Arbeitsflaeche ich={stand.ich} umgebung={stand.umgebung} /></Hinweise>;
}
